// File: frontend/src/components/ums/UserDetailsModal.js

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Mail, Phone, Calendar, Activity, Building, MapPin } from 'lucide-react';
import LoadingSpinner from '../utility/LoadingSpinner';
import styles from './UserDetailsModal.module.css';

const UserDetailsModal = ({ 
  isOpen, 
  onClose, 
  userDetails, 
  isLoading 
}) => {
  // Scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatUserName = (user) => {
    if (!user) return 'Unknown User';
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.is_anonymous ? 'Anonymous User' : 'No Name';
  };

  const getBadgeClass = (value, type) => {
    if (type === 'status') {
      switch (value) {
        case 'active': return styles.badgeSuccess;
        case 'inactive': return styles.badgeWarning;
        case 'suspended': return styles.badgeError;
        default: return styles.badgeDefault;
      }
    }
    if (type === 'role') {
      if (value === 'super_admin' || value === 'admin') return styles.badgeError;
      if (value === 'manager' || value === 'advisor') return styles.badgeInfo;
      return styles.badgeDefault;
    }
    if (type === 'anonymous') {
      return value ? styles.badgeWarning : styles.badgeSuccess;
    }
    return styles.badgeDefault;
  };

  const formatRoleLabel = (role) => {
    if (!role) return '-';
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const modalContent = (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <User size={24} />
            <h2 className={styles.modalTitle}>User Details</h2>
          </div>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody} data-lenis-prevent>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <LoadingSpinner size="large" text="Loading user details..." />
            </div>
          ) : userDetails ? (
            <>
              {/* Basic Information */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <User size={18} />
                  Basic Information
                </h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>User ID</span>
                    <span className={styles.detailValue}>{userDetails.user_id}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Name</span>
                    <span className={styles.detailValue}>{formatUserName(userDetails)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Email</span>
                    <span className={styles.detailValue}>
                      {userDetails.email_address}
                      {userDetails.is_email_verified && (
                        <span className={styles.verifiedBadge} title="Verified">✓</span>
                      )}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Phone</span>
                    <span className={styles.detailValue}>
                      {userDetails.phone_number || '-'}
                      {userDetails.phone_number && userDetails.is_phone_verified && (
                        <span className={styles.verifiedBadge} title="Verified">✓</span>
                      )}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Status</span>
                    <span className={`${styles.badge} ${getBadgeClass(userDetails.user_status, 'status')}`}>
                      {userDetails.user_status}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Role</span>
                    <span className={`${styles.badge} ${getBadgeClass(userDetails.user_role, 'role')}`}>
                      {formatRoleLabel(userDetails.user_role)}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Anonymous</span>
                    <span className={`${styles.badge} ${getBadgeClass(userDetails.is_anonymous, 'anonymous')}`}>
                      {userDetails.is_anonymous ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Journey Stage</span>
                    <span className={styles.detailValue}>{userDetails.journey_stage}</span>
                  </div>
                </div>
              </section>

              {/* Activity Statistics */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Activity size={18} />
                  Activity Statistics
                </h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Total Sessions</span>
                    <span className={styles.detailValue}>
                      {userDetails.session_stats?.total_sessions || userDetails.session_count || 0}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Page Views</span>
                    <span className={styles.detailValue}>
                      {userDetails.session_stats?.total_page_views || userDetails.page_views_count || 0}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Last Activity</span>
                    <span className={styles.detailValue}>{formatDate(userDetails.last_activity_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Last Login</span>
                    <span className={styles.detailValue}>{formatDate(userDetails.last_login_at)}</span>
                  </div>
                </div>
              </section>

              {/* Enterprise Memberships */}
              {userDetails.enterprise_memberships && userDetails.enterprise_memberships.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <Building size={18} />
                    Enterprise Memberships ({userDetails.enterprise_memberships.length})
                  </h3>
                  <div className={styles.membershipsList}>
                    {userDetails.enterprise_memberships.map((membership, index) => (
                      <div key={index} className={styles.membershipCard}>
                        <div className={styles.membershipId}>{membership.enterprise_id}</div>
                        <div className={styles.membershipDetails}>
                          <span className={`${styles.badge} ${getBadgeClass(membership.enterprise_role, 'role')}`}>
                            {formatRoleLabel(membership.enterprise_role)}
                          </span>
                          <span className={`${styles.badge} ${membership.status === 'active' ? styles.badgeSuccess : styles.badgeWarning}`}>
                            {membership.status}
                          </span>
                          <span className={styles.membershipDate}>
                            Joined: {formatDate(membership.joined_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Journey Timeline */}
              {userDetails.journey_timeline && userDetails.journey_timeline.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <MapPin size={18} />
                    Journey Timeline ({userDetails.journey_timeline.length})
                  </h3>
                  <div className={styles.timeline}>
                    {userDetails.journey_timeline.map((item, index) => (
                      <div key={index} className={styles.timelineItem}>
                        <div className={styles.timelineDot}></div>
                        <div className={styles.timelineContent}>
                          <div className={styles.timelineLabel}>{item.label}</div>
                          <div className={styles.timelineTime}>{formatDate(item.timestamp)}</div>
                          {item.description && (
                            <div className={styles.timelineDesc}>{item.description}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Recent Events */}
              {userDetails.recent_events && userDetails.recent_events.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <Activity size={18} />
                    Recent Events ({userDetails.recent_events.length})
                  </h3>
                  <div className={styles.eventsList}>
                    {userDetails.recent_events.map((event) => (
                      <div key={event.event_id} className={styles.eventCard}>
                        <div className={styles.eventType}>
                          {event.event_type.replace(/_/g, ' ')}
                        </div>
                        <div className={styles.eventTime}>
                          {formatDate(event.event_timestamp)}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Attribution */}
              {(userDetails.utm_source || userDetails.referrer_url) && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <MapPin size={18} />
                    Attribution
                  </h3>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>UTM Source</span>
                      <span className={styles.detailValue}>{userDetails.utm_source || '-'}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>UTM Medium</span>
                      <span className={styles.detailValue}>{userDetails.utm_medium || '-'}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>UTM Campaign</span>
                      <span className={styles.detailValue}>{userDetails.utm_campaign || '-'}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Referrer</span>
                      <span className={styles.detailValue}>{userDetails.referrer_url || '-'}</span>
                    </div>
                  </div>
                </section>
              )}

              {/* Timestamps */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Calendar size={18} />
                  Timestamps
                </h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created</span>
                    <span className={styles.detailValue}>{formatDate(userDetails.created_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Updated</span>
                    <span className={styles.detailValue}>{formatDate(userDetails.updated_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Sign Up Completed</span>
                    <span className={styles.detailValue}>{formatDate(userDetails.signup_completed_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Account Activated</span>
                    <span className={styles.detailValue}>{formatDate(userDetails.account_activated_at)}</span>
                  </div>
                </div>
              </section>

              {/* Device Info */}
              {userDetails.first_device_info && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <Activity size={18} />
                    First Device Info
                  </h3>
                  <pre className={styles.jsonViewer}>
                    {JSON.stringify(userDetails.first_device_info, null, 2)}
                  </pre>
                </section>
              )}
            </>
          ) : (
            <div className={styles.errorContainer}>
              <p>Failed to load user details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default UserDetailsModal;